/**
 * Table Policy
 *
 * Single source of truth for all table-related rules and constants.
 * Update here to change behaviour across parsing, projection, and rendering.
 *
 * The LLM prompt fragment (TABLE_TRUST_POLICY_PROMPT) lives in ./tablePolicyPrompt.js
 * so it can also be required by the Node.js backend without a TypeScript compiler.
 */
// eslint-disable-next-line @typescript-eslint/no-var-requires
export const { TABLE_TRUST_POLICY_PROMPT } = require('./tablePolicyPrompt') as {
  TABLE_TRUST_POLICY_PROMPT: string;
};

// ---------------------------------------------------------------------------
// Missing-value sentinels
// ---------------------------------------------------------------------------

/** Primitive values that are always treated as empty cells. */
export const EMPTY_CELL_VALUES: (string | null | undefined)[] = ['', null, undefined];

/** Case-insensitive strings that are treated as empty cells. */
export const EMPTY_CELL_STRING_VALUES: string[] = [
  'na', 'n/a', 'not specified', 'not available', 'not stated', 'tbd', 'tba',
  '-', '—', '–',
];

// ---------------------------------------------------------------------------
// Column-header patterns
// ---------------------------------------------------------------------------

/** Matches "Source" or "Sources" headers (case-insensitive). */
export const SOURCE_HEADER_PATTERN = /^sources?$/i;

/**
 * Matches numeric-like cell values that should be treated as data points:
 * optional leading whitespace / qualifier symbols, digits, optional unit.
 * Examples: "3.6", "$42.5B", "12 %", "~100", "€1,234.56"
 */
export const NUMERIC_WITH_OPTIONAL_UNIT_PATTERN =
  /^[\s~<>≈$€£¥]*\d[\d,]*(\.\d+)?(\s*[a-zA-Z%]+)?(\s*\/\s*\d[\d,]*(\.\d+)?(\s*[a-zA-Z%]+)?)?\s*$/;

// ---------------------------------------------------------------------------
// Cell metadata defaults
// ---------------------------------------------------------------------------

/** Fallback tooltip text for filled/projected cells that lack a formula. */
export const DEFAULT_CALCULATION_DETAILS = 'Calculated value';

// ---------------------------------------------------------------------------
// Source parsing policy
// ---------------------------------------------------------------------------

/**
 * When true, a source cell that does not contain an HTML anchor (<a href>)
 * is discarded during markdown-table parsing.
 * This prevents plain-text source labels from being stored without a URL,
 * which would otherwise cause numeric cells to appear as unverified.
 */
export const SOURCE_CELL_REQUIRES_HREF = true;

// ---------------------------------------------------------------------------
// Display policy
// ---------------------------------------------------------------------------

/**
 * When true, numeric cells without a verified source URL are hidden in the UI.
 * Set to false to show all values regardless of source verification status.
 */
export const HIDE_UNVERIFIED_BY_DEFAULT = true;
